// Mock data for e-health landing page

export const mockContactSubmissions = [];

export const submitContactForm = async (formData) => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 1000));
  
  // Add to mock submissions
  const submission = {
    id: Date.now(),
    ...formData,
    submittedAt: new Date().toISOString()
  };
  
  mockContactSubmissions.push(submission);
  
  return {
    success: true,
    message: 'Thank you! We will contact you within 24 hours.',
    data: submission
  };
};

export const languages = [
  { code: 'en', label: 'EN' },
  { code: 'fr', label: 'FR' },
  { code: 'de', label: 'DE' },
  { code: 'lv', label: 'LV' },
  { code: 'lt', label: 'LT' },
  { code: 'ee', label: 'EE' }
];

export const heroCarouselImages = [
  'https://images.unsplash.com/photo-1640249306698-28854d875b76',
  'https://images.unsplash.com/photo-1638734255280-8bae834f8297',
  'https://images.pexels.com/photos/32079706/pexels-photo-32079706.jpeg',
  'https://images.unsplash.com/photo-1551288049-bebda4e38f71'
];

export const challengesData = [
  {
    id: 1,
    image: '/images/challenges/challenges-1.png',
    title: 'Rising Operational Costs',
    description: 'Staff lose time searching equipment, performing unnecessary visits and managing manual logs.'
  },
  {
    id: 2,
    image: '/images/challenges/challenges-2.png',
    title: 'Lack of Real-Time Visibility',
    description: 'Late detection of risks, wandering patients, unseen seniors for hours.'
  },
  {
    id: 3,
    image: '/images/challenges/challenges-3.png',
    title: 'Staff Overload',
    description: 'Too many tasks, too few people. E2 automates and simplifies.'
  }
];

export const solutionsData = [
  {
    id: 'elderly',
    title: 'Elderly Safety & Home Monitoring',
    images: [
      '/images/elderly/elderly-1.png',
      '/images/elderly/elderly-5.png',
      '/images/elderly/elderly-3.png',
      '/images/elderly/elderly-7.png'
    ],
    description: 'Fall detection, GPS safety, behaviour insights, family alerts, Alexa voice help.',
    link: '/elderly'
  },
  {
    id: 'hospital',
    title: 'Hospital Patient & Equipment tracking',
    images: [
      '/images/solutions/hospital-tracker-1.png',
      '/images/solutions/hospital-tracker-2.png',
      '/images/solutions/hospital-tracker-3.png',
      '/images/solutions/hospital-tracker-4.png'
    ],
    description: 'Track patients, beds, wheelchairs and pumps in real time.',
    link: '/hospitals'
  },
  {
    id: 'environment',
    title: 'Environment & Comfort Monitoring',
    images: [
      '/images/environment/environment-1.png',
      '/images/environment/environment-2.png'
    ],
    description: 'CO₂, temperature, humidity, sleep patterns.',
    link: '/environment'
  },
  {
    id: 'platform',
    title: 'Caregiver & Medical Platform (eDoctor)',
    images: [
      'https://images.unsplash.com/photo-1551288049-bebda4e38f71',
      'https://images.unsplash.com/photo-1758691463610-3c2ecf5fb3fa'
    ],
    description: 'Alerts, notes, trends, follow-up tools — available in LV, LT, EE.',
    link: '/platform'
  }
];

export const devicesData = [
  { id: 1, name: 'Asset Tracker', image: '/images/devices/asset-tracker.png' },
  { id: 2, name: 'Smart Badge', image: '/images/devices/smart-badge.png' },
  { id: 3, name: 'Signal Repeater', image: '/images/devices/signal-repeater.png' },
  { id: 4, name: 'Patient Wristband', image: '/images/devices/patient-wristband.png' },
  { id: 5, name: 'Air Quality Sensor', image: '/images/devices/air-quality-sensor.png' },
  { id: 6, name: 'Panic Button', image: '/images/devices/panic-button.png' },
  { id: 7, name: 'Indoor Patient Locator', image: '/images/devices/indoor-patient-locator.png' },
  { id: 8, name: 'Smart Energy Counter', image: '/images/devices/smart-energy-counter.png' },
  { id: 9, name: 'Other Sensors', image: 'https://images.unsplash.com/photo-1638734254958-4a11c989e9bb' }
];

export const outcomesData = [
  {
    id: 1,
    title: 'Reduce Costs',
    image: '/images/outcome/outcome-1.png',
    bullets: [
      'Fewer unnecessary visits',
      'Fewer equipment losses',
      'Automated logs',
      'Fewer emergency incidents'
    ]
  },
  {
    id: 2,
    title: 'Improve Quality of Care',
    image: '/images/outcome/outcome-2.png',
    bullets: [
      'Real-time alerts',
      'Early risk detection',
      'Transparency for families',
      'Better comfort'
    ]
  },
  {
    id: 3,
    title: 'Increase Staff Efficiency',
    image: '/images/outcome/outcome-3.png',
    bullets: [
      'Unified dashboards',
      'WhatsApp alerts',
      'Voice assistance (Alexa local)'
    ]
  }
];

export const pilotCards = [
  {
    id: 1,
    title: 'Elder Safety & Home Monitoring',
    image: '/images/pilot/pilot-1.jpg',
    description: 'GPS Watch, eBeat, PIR, family alerts.',
    buttonText: 'Start Elder Pilot'
  },
  {
    id: 2,
    title: 'Hospital Tracking',
    image: '/images/pilot/pilot-2.jpg',
    description: 'Patient locator, bed tracking, asset tracking.',
    buttonText: 'Start Hospital Pilot'
  },
  {
    id: 3,
    title: 'Environment Monitoring',
    image: '/images/pilot/pilot-3.jpg',
    description: 'CO₂, humidity, temperature, sleep insights.',
    buttonText: 'Start Environment Pilot'
  },
  {
    id: 4,
    title: 'Full Ecosystem',
    image: '/images/pilot/pilot-4.jpg',
    description: 'All devices + dashboards + apps.',
    buttonText: 'Start Full Pilot'
  }
];

export const faqData = [
  {
    id: 1,
    question: 'Is the platform GDPR compliant?',
    answer: 'Yes — EU-hosted, privacy-by-design.'
  },
  {
    id: 2,
    question: 'Are the apps available in LV, LT, EE?',
    answer: 'Yes — full localisation for seniors, caregivers and hospitals.'
  },
  {
    id: 3,
    question: 'How fast can deployment happen?',
    answer: '24–48 hours depending on scope.'
  },
  {
    id: 4,
    question: 'Do you support both elderly at home and hospitals?',
    answer: 'Yes — one ecosystem for both.'
  },
  {
    id: 5,
    question: 'Are WhatsApp alerts supported?',
    answer: 'Yes — they go directly to caregivers and family.'
  },
  {
    id: 6,
    question: 'Is the pilot contract-free?',
    answer: 'Yes — no commitment.'
  }
];
